import { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MarketingLayout } from './routes/marketing/MarketingLayout';
import { AuthLayout } from './routes/auth/AuthLayout';
import { Login } from './routes/auth/Login';
import { MfaVerify } from './routes/auth/MfaVerify';
import { Register } from './routes/auth/Register';
import { Reset } from './routes/auth/Reset';
import { ChangePassword } from './routes/auth/ChangePassword';
import { VerifyEmail } from './routes/auth/VerifyEmail';
import { AppLayout } from './routes/app/AppLayout';
import { Dashboard } from './routes/app/Dashboard';
import { Module } from './routes/app/Module';
import { Report } from './routes/app/Report';
import { LiveView } from './routes/app/report/LiveView';
import { SnapshotsList } from './routes/app/report/SnapshotsList';
import { SnapshotView } from './routes/app/report/SnapshotView';
import { SnapshotCompare } from './routes/app/report/SnapshotCompare';
import { AgencyDrilldown } from './routes/app/report/AgencyDrilldown';
import { Settings } from './routes/app/Settings';
import { AccountTab } from './routes/app/settings/AccountTab';
import { CompanyTab } from './routes/app/settings/CompanyTab';
import { SubscriptionTab } from './routes/app/settings/SubscriptionTab';
import { SupportTab } from './routes/app/settings/SupportTab';
import { TeamTab } from './routes/app/settings/TeamTab';
import { AdminRoute } from './components/AdminRoute';

// Marketing routes are code-split. Each chunk is fetched on demand, which
// keeps the initial bundle small and matches the prerender boundary used by
// the prerender crawl (web/scripts/prerender.mjs — Suspense resolves before
// snapshot capture).
//
// Auth + product routes stay synchronous: they're behind login, the user
// has already paid the cost of "the app loaded," and lazy boundaries here
// would just add navigation latency for negligible payload savings.
const Home = lazy(() =>
  import('./routes/marketing/Home').then((m) => ({ default: m.Home })),
);
const About = lazy(() =>
  import('./routes/marketing/About').then((m) => ({ default: m.About })),
);
const Pricing = lazy(() =>
  import('./routes/marketing/Pricing').then((m) => ({ default: m.Pricing })),
);
const Agencies = lazy(() =>
  import('./routes/marketing/Agencies').then((m) => ({ default: m.Agencies })),
);
const Contact = lazy(() =>
  import('./routes/marketing/Contact').then((m) => ({ default: m.Contact })),
);
const HowItWorks = lazy(() =>
  import('./routes/marketing/HowItWorks').then((m) => ({
    default: m.HowItWorks,
  })),
);
const Terms = lazy(() =>
  import('./routes/marketing/Legal').then((m) => ({ default: m.Terms })),
);
const Privacy = lazy(() =>
  import('./routes/marketing/Legal').then((m) => ({ default: m.Privacy })),
);
const Blog = lazy(() =>
  import('./routes/marketing/Blog').then((m) => ({ default: m.Blog })),
);
const BlogPost = lazy(() =>
  import('./routes/marketing/Blog').then((m) => ({ default: m.BlogPost })),
);
const Trust = lazy(() =>
  import('./routes/marketing/Trust').then((m) => ({ default: m.Trust })),
);
const LibraryIndex = lazy(() =>
  import('./routes/marketing/library/LibraryIndex').then((m) => ({
    default: m.LibraryIndex,
  })),
);
const AgencyView = lazy(() =>
  import('./routes/marketing/library/AgencyView').then((m) => ({
    default: m.AgencyView,
  })),
);
const StandardDetail = lazy(() =>
  import('./routes/marketing/library/StandardDetail').then((m) => ({
    default: m.StandardDetail,
  })),
);
const CostPage = lazy(() =>
  import('./routes/marketing/cost-of-non-compliance/CostPage').then((m) => ({
    default: m.CostPage,
  })),
);
const ScorecardLanding = lazy(() =>
  import('./routes/marketing/scorecard/Landing').then((m) => ({
    default: m.Landing,
  })),
);
const ScorecardQuestion = lazy(() =>
  import('./routes/marketing/scorecard/Question').then((m) => ({
    default: m.Question,
  })),
);
const ScorecardResult = lazy(() =>
  import('./routes/marketing/scorecard/Result').then((m) => ({
    default: m.Result,
  })),
);
const NotFound = lazy(() =>
  import('./routes/marketing/NotFound').then((m) => ({ default: m.NotFound })),
);

// Admin routes are code-split even though they live behind login: they're
// only ever loaded by the one or two admin users, so everyone else skips
// the chunk entirely. Not a security boundary — the API enforces roles.
const AdminLayout = lazy(() =>
  import('./routes/app/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminBlogList = lazy(() =>
  import('./routes/app/admin/BlogList').then((m) => ({ default: m.BlogList })),
);
const AdminBlogEditor = lazy(() =>
  import('./routes/app/admin/BlogEditor').then((m) => ({ default: m.BlogEditor })),
);
const AdminSummaryReview = lazy(() =>
  import('./routes/app/admin/SummaryReview').then((m) => ({ default: m.SummaryReview })),
);
const AdminReviewList = lazy(() =>
  import('./routes/app/admin/ReviewList').then((m) => ({ default: m.ReviewList })),
);
const AdminStandardHistory = lazy(() =>
  import('./routes/app/admin/StandardHistory').then((m) => ({ default: m.StandardHistory })),
);
const AdminItemHistory = lazy(() =>
  import('./routes/app/admin/ItemHistory').then((m) => ({ default: m.ItemHistory })),
);
const AdminEnforcementList = lazy(() =>
  import('./routes/app/admin/EnforcementList').then((m) => ({ default: m.EnforcementList })),
);
const AdminEnforcementEditor = lazy(() =>
  import('./routes/app/admin/EnforcementEditor').then((m) => ({ default: m.EnforcementEditor })),
);
const AdminReleases = lazy(() =>
  import('./routes/app/admin/ReleasesPage').then((m) => ({ default: m.ReleasesPage })),
);
const AdminReviewDetail = lazy(() =>
  import('./routes/app/admin/ReviewDetail').then((m) => ({ default: m.ReviewDetail })),
);
const AdminAudit = lazy(() =>
  import('./routes/app/admin/AuditPage').then((m) => ({ default: m.AuditPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            {/* Marketing */}
            <Route element={<MarketingLayout />}>
              <Route index element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/agencies" element={<Agencies />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/library" element={<LibraryIndex />} />
              <Route path="/library/:agencyCode" element={<AgencyView />} />
              <Route
                path="/library/:agencyCode/:standardCode"
                element={<StandardDetail />}
              />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/scorecard" element={<ScorecardLanding />} />
              <Route path="/scorecard/q/:n" element={<ScorecardQuestion />} />
              <Route path="/scorecard/result" element={<ScorecardResult />} />
              <Route path="/cost-of-non-compliance" element={<CostPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/terms-of-service" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Auth */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/mfa-verify" element={<MfaVerify />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>

            {/* App (auth-gated by AppLayout) */}
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/module/:moduleId" element={<Module />} />
              <Route path="/report" element={<Report />}>
                <Route index element={<LiveView />} />
                <Route path="snapshots" element={<SnapshotsList />} />
                <Route path="compare" element={<SnapshotCompare />} />
                <Route path="agency/:code" element={<AgencyDrilldown />} />
                <Route path="snapshots/:snapshotId" element={<SnapshotView />} />
              </Route>
              <Route path="/settings" element={<Settings />}>
                <Route index element={<Navigate to="account" replace />} />
                <Route path="account" element={<AccountTab />} />
                <Route path="company" element={<CompanyTab />} />
                <Route path="team" element={<TeamTab />} />
                <Route path="subscription" element={<SubscriptionTab />} />
                <Route path="support" element={<SupportTab />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<Navigate to="review" replace />} />
                <Route path="blog" element={<AdminBlogList />} />
                <Route path="blog/new" element={<AdminBlogEditor />} />
                <Route path="blog/:id" element={<AdminBlogEditor />} />
                <Route path="review" element={<AdminReviewList />} />
                <Route path="summaries" element={<AdminSummaryReview />} />
                <Route path="review/:id" element={<AdminReviewDetail />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="releases" element={<AdminReleases />} />
                <Route path="enforcement" element={<AdminEnforcementList />} />
                <Route path="enforcement/new" element={<AdminEnforcementEditor />} />
                <Route path="enforcement/:id" element={<AdminEnforcementEditor />} />
                <Route path="standards/:id/history" element={<AdminStandardHistory />} />
                <Route path="items/:id/history" element={<AdminItemHistory />} />
              </Route>
            </Route>

          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
